import { useEffect, useRef, useState } from "react";
import {
  IonItem,
  IonProgressBar,
  IonLabel,
  IonList,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonIcon,
  IonSpinner,
} from "@ionic/react";
import axios from "axios";
import { isArray } from "lodash";
import { FileUploader } from "react-drag-drop-files";
import { attachOutline } from "ionicons/icons";
import { HtmlText } from "pymble-ui/components";

import { usePymbleApi } from "../../hooks/usePymbleApi";

import "./CoversFileUploader.css";

// const FILE_STORAGE_END_POINT = `${api.myPymble}/filestorage`;
const FILE_STORAGE_END_POINT = "/liss/service/upload/";
const fileTypes = [
  // "JPG",
  // "PNG",
  // "GIF",
  // "MOV",
  // "MP4",
  // "PDF",
  // "TXT",
  // "DOC",
  // "DOCX",
  // "ZIP",
  // "XLS",
  // "XLSX",
  "CSV",
  // "PPTX",
];



type ProgressInfo = {
  percentage: number;
  fileName: string;
};
type ProgressInfos = {
  val: ProgressInfo[];
};

// React component that is called UploadError
// UploadError:
//   A component that displays an error message when a file upload fails.
const UploadError = ({ message }: { message: string }) => {
  return (
    <span className="upload-error"><HtmlText htmltext={message} /></span>
  );
};

const UploadSuccess = ({ message }: { message: string }) => {
  return (
    <span className="upload-success"><HtmlText htmltext={message} /></span>
  );
};

// FileUploadSettings:
//   Settings for file upload.
export type FileUploadSettings = {
  // Type of data being uploaded (eg."edval-absences")
  DataType?: string;

};

// Default settings for file upload
const settingDefaults = {

};

interface CoversFileUploaderProps {
  settings: FileUploadSettings;
  showMessages?: boolean;
  onUploadComplete?: (files: any[]) => void;
}
/**
 * CoversFileUploader
 *
 * A file uploader component that uses the Pymble API to upload files to Azure Storage.
 *
 * Drag and drop files onto the drop zone or click the button to select files.
 *
 * - Files are uploaded to Pymble API endpoint.
 * - A progress bar is displayed for each file being uploaded.
 * - Once complete, the onUploadComplete callback is called.
 * - Settings for the file upload are passed in via the settings prop. See FileUploadSettings.
  * - showMessages is a boolean that determines whether messages are displayed to the user.
 */
const CoversFileUploader: React.FC<CoversFileUploaderProps> = ({
  settings,
  showMessages = false,
  onUploadComplete,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [progressInfos, setProgressInfos] = useState<ProgressInfos>({
    val: [],
  });
  const [message, setMessage] = useState<any[]>([]);
  const [dropZoneActive, setDropZoneActive] = useState<boolean>(false);
  const progressInfosRef = useRef<any>({ val: [] });
  const [showSpinner, setShowSpinner] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fileSettings: FileUploadSettings = { ...settingDefaults, ...settings };

  const { callApiS2: callApi } = usePymbleApi();

  const handleFileChange = (file: FileList) => {
    let files = Array.from(file);
    resetProgressInfos();
    resetMessages();
    setSelectedFiles(files);
  };

  const resetProgressInfos = () => {
    setProgressInfos({ val: [] });
  };
  const resetMessages = () => {
    setMessage([]);
  };
  const resetFiles = () => {
    setSelectedFiles([]);
  };
  const resetUpload = () => {
    resetFiles();
    resetProgressInfos();
    resetMessages();
  };

  // Upload files
  // Uses the FileStorage API to upload files to Azure Storage.
  // The API returns the file metadata which is stored in the database.
  // A progress bar is displayed for each file via an array of ProgressInfo objects.
  const upload = async (idx: number, file: File) => {
    if (!progressInfosRef?.current) {
      console.warn("CoversFileUploader: upload: progressInfosRef is null");
    }
    // eslint-disable-next-line no-unsafe-optional-chaining
    let _progressInfos = [...progressInfosRef?.current?.val];

    let formData = new FormData();

    formData.append("file", file);

    try {
      if (settings.DataType) {
        let uploadResult = null;
        setShowSpinner(true);
        try {
          uploadResult = await callApi(
            "POST",
            FILE_STORAGE_END_POINT,
            {
              data: formData,
              headers: {
                "Content-Type": "multipart/form-data",
              },
              onUploadProgress: (event: any) => {
                const progress = Math.round((100 * event.loaded) / event.total);
                _progressInfos[idx].percentage = progress;
                setProgressInfos({ val: _progressInfos });
              },
            }
          );

          let msg = "";
          let extraMsg = "";
          if (uploadResult?.message) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            msg = uploadResult?.message;
            if (isArray(uploadResult?.processed)) {
              extraMsg = "<ul>";
              uploadResult?.processed.forEach((m: string) => {
                extraMsg += "<li>" + m + "</li>";
              });
              extraMsg += "</ul>";
            }
          }

          setMessage([
            ...message,
            <UploadSuccess message={"Uploaded the file successfully: " + file.name + "<br>" + extraMsg} />,
          ]);
        }
        catch (error) {
          console.error("CoversFileUploader: upload: error", error);
          setShowSpinner(false);
          // Check if we have a value in error.response.data and log it if we do
          let extraMsg = "";
          let processedMsg = "";
          let exceptionMsg = "";
          if (axios.isAxiosError(error)) {
            if (error?.response?.data) {
              console.error("CoversFileUploader: upload: error.response.data", error.response.data);
              extraMsg = error.response.data?.detail?.message;
              if (isArray(error.response.data?.detail?.errors)) {
                // Add the errors as a list
                extraMsg = "<ul>";
                error.response.data?.detail?.errors.forEach((err: string) => {
                  extraMsg += "<li>" + err + "</li>";
                });
                extraMsg += "</ul>";
              }

              if (isArray(error.response.data?.detail?.processed)) {
                processedMsg = "The following teachers were processed: " + error.response.data?.detail?.processed.join(", ") + "<br>";
              }
              if (error.response.data?.detail?.exception) {
                exceptionMsg = "Exception: " + error.response.data?.detail?.exception;
              }
            }
          }
          setMessage([
            ...message,
            <UploadError message={"There were problems in the file: " + file.name + "<br>" + extraMsg} />,
            exceptionMsg && <UploadError message={exceptionMsg} />,
            processedMsg && <UploadSuccess message={processedMsg} />
          ]);
        }
        setShowSpinner(false);
        return uploadResult;
      } else {
        console.warn("CoversFileUploader: upload: id or collection is not set");
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      _progressInfos[idx].percentage = 0;
      setProgressInfos({ val: _progressInfos });
      setMessage([...message, <UploadError message={"There were problems in the file: " + file.name} />]);
      setShowSpinner(false);
    }
    return null;
  };

  // Upload files triggered by the upload button
  // Uploads the files in the selectedFiles array.
  // The upload function returns the file metadata which is stored in the database.
  const uploadFiles = async () => {
    const files = Array.from(selectedFiles);

    let __progressInfos: ProgressInfo[] = files.map((file: File) => ({
      percentage: 0,
      fileName: file?.name,
    }));

    if (progressInfosRef?.current) {
      progressInfosRef.current.val = __progressInfos;
    } else {
      console.warn(
        "CoversFileUploader 2: uploadFiles: progressInfosRef is null"
      );
    }

    const uploadPromises = files.map((file, i) => upload(i, file));

    const results = await Promise.all(uploadPromises);

    let fileMetaResults: any[] = [];
    for (let i = 0; i < results.length; i++) {
      fileMetaResults[i] = results[i]?.File;
    }

    // Call onUploadComplete
    console.log("File Upload Results:", fileMetaResults);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onUploadComplete && onUploadComplete(fileMetaResults);

    return fileMetaResults;
  };

  useEffect(() => {
    if (selectedFiles.length > 0) {
      uploadFiles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles]);

  return (
    <div
      className={
        "covers-file-uploader" +
        (dropZoneActive ? " file-drop-zone-active" : "")
      }
    >
      <IonGrid>
        <IonRow>
          {progressInfos && progressInfos.val.length > 0 && (
            <IonCol>
              {progressInfos.val.map((pInfo, index) => (
                <IonItem key={"prog-" + index}>
                  <IonLabel slot="start">{pInfo.fileName}</IonLabel>
                  <IonProgressBar
                    buffer={0.06}
                    value={pInfo.percentage / 100}
                  ></IonProgressBar>
                </IonItem>
              ))}
            </IonCol>
          )}
          <IonCol>
            <FileUploader
              handleChange={handleFileChange}
              name="file"
              types={fileTypes}
              multiple={true}
              className="file-uploader-target"
              onDraggingStateChange={(dragging: boolean) => {
                setDropZoneActive(dragging);
              }}
              hoverTitle={" "}
            >
              <div className="uploader-div">
                <IonButton
                  fill="outline"
                  size="large"
                  className="ion-text-capitalize file-uploader-button"
                  shape="round"
                  expand="block"
                >
                  <IonIcon icon={attachOutline} slot="start" />
                  {"Drag Edval .csv file here or click to select"}
                </IonButton>
                <div className="uploader-msg">
                  {"File must be .csv exported from the Daily Absencs tab in Edval"}
                </div>
              </div>
            </FileUploader>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            {showSpinner && <IonSpinner className="covers-file-upload-spinner" name="crescent" color="primary" />}
            {showMessages && message.length > 0 && (
              <IonList className="file-uploader-messages">
                {message.map((item, i) => {
                  return (
                    <IonItem key={i}>
                      <li>{item}</li>
                    </IonItem>
                  );
                })}
              </IonList>
            )}
          </IonCol>
        </IonRow>
        {(message.length > 0 || progressInfos?.val?.length > 0) && (
          <IonRow>
            <IonCol>
              <IonItem lines="none">
                <IonButton
                  slot="end"
                  fill="clear"
                  size="small"
                  onClick={resetUpload}
                >
                  {"Clear Upload Status"}
                </IonButton>
              </IonItem>
            </IonCol>
          </IonRow>
        )}
      </IonGrid>
    </div>
  );
};

export default CoversFileUploader;
